import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { Control } from "../../UI.BaseControls/Control";
import { GenericControl } from "../../UI.BaseControls/GenericControl";
import { EventHelper } from "../../UI.BaseControls/Helpers/EventHelper";
import { Action } from "../../UI.Controls/Actions/Action";
import { DropDownMenuHorizontalOpen, IDropDownMenuItem } from "../../UI.Controls/DropDownMenu/DropDownInterfaces";
import { DropDownMenu } from "../../UI.Controls/DropDownMenu/DropDownMenu";
import { DropDownMenuItem } from "../../UI.Controls/DropDownMenu/DropDownMenuItem";
import { SeparatorMenuItem } from "../../UI.Controls/DropDownMenu/SeparatorItem";

const logger = LazyLoadingSectionLogger.create(LoggingSection.fileEdit);

export class FileMenuBase {
    private _createdMenuItems: Map<MenuItem, (Control & IDropDownMenuItem)[]> = new Map<MenuItem, (Control & IDropDownMenuItem)[]>();
    private _menuItemsFactories: Map<MenuItem, () => Control & IDropDownMenuItem> = new Map<MenuItem, () => Control & IDropDownMenuItem>();
    protected _signMenuItemsSeparators: SeparatorMenuItem[];
    protected _fileMenu: DropDownMenu;
    public type: FileMenuType;

    public get isOpened(): boolean {
        return this._fileMenu.isOpened;
    }

    public show(startElement: HTMLElement, event: Event): void {
        this._fileMenu.show(startElement, event);
    }

    public showEmpty(startElement: HTMLElement, event: Event): void {
        this._fileMenu.showEmpty(startElement, event);
    }

    public close(dontOpenOnNextClick: boolean, event: Event): void {
        this._fileMenu.close(dontOpenOnNextClick);
        if (event)
            EventHelper.stopPropagation(event);
    }

    private _registerMenuItemFactory(menuType: MenuItem, handler: () => Promise<void>, icon: string, locKey: string, classes?: string[], enabled: boolean = true): void {
        const creator = () => {
            const element = new DropDownMenuItem();
            const action = new Action(handler, locKey);

            element.action = action;
            action.enabled = enabled;

            element.icon = icon;
            if (classes)
                element.classList.add(...classes);
            //zaregistrujeme vytvořený typ elementu, aby ho pak bylo možné získat podle typu.
            if (!this._createdMenuItems.has(menuType))
                this._createdMenuItems.set(menuType, []);
            this._createdMenuItems.get(menuType).push(element);

            return element;
        };
        //element.LocKey = locKey;
        this._menuItemsFactories.set(menuType, creator);
    }

    protected _createMenuItem(menuItemType: MenuItem): Control & IDropDownMenuItem {
        if (this._menuItemsFactories.has(menuItemType))
            return this._menuItemsFactories.get(menuItemType)();
        else {
            logger.error(`Unable to create menu Item of type '${menuItemType}'. Register its factory before creating.`);
            return undefined;
        }
    }
    private *_getMenuItemsOfType(...menuItemTypes: MenuItem[]): IterableIterator<Control & IDropDownMenuItem> {
        for (const menuItemType of menuItemTypes) {
            if (this._createdMenuItems.has(menuItemType))
                yield* this._iterateArray(this._createdMenuItems.get(menuItemType));
        }
    }
    private *_iterateArray(array: (Control & IDropDownMenuItem)[]): Generator<Control & IDropDownMenuItem, void, unknown> {
        for (const item of array)
            yield item;
    }

    protected _registrMenuItem(): void {
        this._registerMenuItemFactory(MenuItemFile.upload, async () => {
            this._callEvent("triggerOpenFile", { withSigning: false });
        }, "upload", "GUI-600106", ["upload"]); // Nahrát
        this._registerMenuItemFactory(MenuItemFile.download, async () => {
            this._callEvent("downloadRequest");
        }, "download", "GUI-397994", ["download"]); // Stáhnout soubor
        this._registerMenuItemFactory(MenuItemFile.downloadArchiveVersion, async () => {
            this._callEvent("downloadRequest", { isArchiveDocument: true });
        }, "download", "GUI-667542", ["archive-version"]); // Stáhnout archivní verzi
        this._registerMenuItemFactory(MenuItemFile.copyUrl, async () => {
            this._callEvent("copyUrl");
        }, "web-link", "GUI-667747", ["copy-url"]); // Kopírovat odkaz na soubor
        this._registerMenuItemFactory(MenuItemFile.showVersions, async () => {
            this._callEvent("showVersions");
        }, "doc-archive", "GUI-678599", ["show-versions"]); // Verze dokumentu
        this._registerMenuItemFactory(MenuItemFile.uploadAnother, async () => {
            this._callEvent("triggerOpenFile");
        }, "upload", "GUI-599591", ["upload-another"]); // Nahrát jiný
        this._registerMenuItemFactory(MenuItemFile.delete, async () => {
            this._callEvent("removeRequest");
        }, "remove", "GUI-314806", ["delete"]); // Smazat
        this._registerMenuItemFactory(MenuItemSigning.uploadAndSign, async () => {
            this._callEvent("triggerOpenFile", { withSigning: true });
        }, "upload", "GUI-625601", ["upload-sign"]); // Nahrát a podepsat
        this._registerMenuItemFactory(MenuItemSigning.uploadAnotherAndSign, async () => {
            this._callEvent("triggerOpenFile", { withSigning: true });
        }, "editorial-pen", "GUI-625602", ["upload-sign-another"]); // Nahrát jiný a podepsat
        this._registerMenuItemFactory(MenuItemSigning.sign, async () => {
            this._callEvent("signRequest");
        }, "editorial-pen", "GUI-604319", ["sign"]); // Vytvořit podpis
        this._signMenuItemsSeparators = [];
    }

    /**
     * Nastavuje, zda je možné vytvářet digitální podpisy dokumentu
     * @internal
     */
    public updateSigningEnabled(signingEnabled: boolean, signingEnabledIfEmpty: boolean = signingEnabled): void {
        // při změně je potřeba skrýt související položky v menu
        for (const i of this._getMenuItemsOfType(MenuItemSigning.sign, MenuItemSigning.uploadAndSign))
            i.visible = signingEnabled;
        for (const e of this._getMenuItemsOfType(MenuItemSigning.uploadAnotherAndSign))
            e.visible = signingEnabledIfEmpty;
        this._signMenuItemsSeparators.forEach(i => i.visible = signingEnabled);
    }

    /**
     * Nastavuje viditelnost akce 'menuItem'.
     *
     * @param value Hodnota udavající zda bude akce MenuItem viditelná.
     * @param menuItem Akce meny, pro kterou se nastavuje viditelnost.
     */
    public setMenuItemEnabled(value: boolean, menuItem: MenuItem): void {
        if (value == undefined)
            return;
        for (const item of this._getMenuItemsOfType(menuItem))
            item.visible = value;
    }

    public setStatus(status: FileEditStatus): void {
        // stav tlačítek pro stažení a podpis je třeba aktualizovat podle dostupnosti dokumentu
        if (status === FileEditStatus.download || status === FileEditStatus.unavailable) {
            for (const item of this._getMenuItemsOfType(MenuItemFile.download, MenuItemFile.downloadArchiveVersion, MenuItemSigning.sign, MenuItemFile.copyUrl, MenuItemFile.showVersions)) {
                if (item instanceof DropDownMenuItem) {
                    item.action.enabled = (status === FileEditStatus.download);
                }
            }
        }
    }

    //#region Zpracování metod z globální třídy GenericControl
    /**
     * Zaregistruje událost
     * @param event Název události
     * @param action Akce při vyvolání události
     */
    public registerEventListener(event: string, action: (e: CustomEvent) => any, _useCapture?: boolean): void {
        GenericControl.registerEventListener(this._fileMenu as HTMLElement, event, action);
    }
    /**
     * Zavolá událost na elementu
     * @param eventName Název události
     * @param detail Podrobnosti události
     * @return Vrátí customEvent
     */
    protected _callEvent(eventName: string, detail?: any, bubbles: boolean = false, cancelable: boolean = false): CustomEvent {
        return GenericControl.callEvent(this._fileMenu as HTMLElement, eventName, detail, bubbles, cancelable);
    }
    //#enregion Zpracování metod z globální třídy GenericControl
}
type MenuItem = MenuItemFile | MenuItemSigning;
export enum MenuItemFile {
    upload = "upload",
    download = "download",
    downloadArchiveVersion = "downloadArchiveVersion",
    copyUrl = "copyUrl",
    uploadAnother = "uploadAnother",
    delete = "delete",
    showVersions = "showVersions",
}
enum MenuItemSigning {
    sign = "sign",
    uploadAndSign = "uploadAndSign",
    uploadAnotherAndSign = "uploadAnotherAndSign",
}

// Standartní menu v editaci - obsahuje pouze položky pro editaci
export class FileMenuEdit extends FileMenuBase {
    constructor() {
        super();
        this._createFileMenuItems();
        this.type = FileMenuType.edit;
    }

    private _createFileMenuItems(): void {
        this._registrMenuItem();

        this._fileMenu = new DropDownMenu();
        this._fileMenu.horizontalOpenPosition = DropDownMenuHorizontalOpen.right;
        this._fileMenu.addItem(this._createMenuItem(MenuItemFile.download)); // Stáhnout soubor

        // archivní verze
        this._fileMenu.addItem(this._createMenuItem(MenuItemFile.downloadArchiveVersion)); // Stáhnout archivní verzi
        this._fileMenu.addItem(this._createMenuItem(MenuItemFile.showVersions)); // Archivní verze

        // kopírovat odkaz
        this._fileMenu.addItem(this._createMenuItem(MenuItemFile.copyUrl)); // kopírovat odkaz na soubor

        this._fileMenu.addItem(this._createMenuItem(MenuItemFile.uploadAnother)); //Nahrát jiný

        // část podpisů
        const separator = new SeparatorMenuItem();
        this._fileMenu.addItem(separator);
        this._signMenuItemsSeparators.push(separator);
        this._fileMenu.addItem(this._createMenuItem(MenuItemSigning.sign)); //Vytvořit podpis

        this._fileMenu.addItem(this._createMenuItem(MenuItemSigning.uploadAnotherAndSign)); // Nahrát jiný a podepsat

        this._fileMenu.addItem(new SeparatorMenuItem());
        this._fileMenu.addItem(this._createMenuItem(MenuItemFile.delete)); //Smazat
    }
}

// Menu pro needitační režim - obsahuje pouze položky, které mají význam mimo editaci
export class FileMenuNonEdit extends FileMenuBase {
    constructor() {
        super();
        this._createFileMenuItems();
        this.type = FileMenuType.nonEdit;
    }

    private _createFileMenuItems(): void {
        this._registrMenuItem();

        this._fileMenu = new DropDownMenu();
        this._fileMenu.horizontalOpenPosition = DropDownMenuHorizontalOpen.right;
        this._fileMenu.addItem(this._createMenuItem(MenuItemFile.download)); // Stáhnout soubor

        // archivní verze
        this._fileMenu.addItem(this._createMenuItem(MenuItemFile.downloadArchiveVersion)); // Stáhnout archivní verzi
        this._fileMenu.addItem(this._createMenuItem(MenuItemFile.showVersions)); // Archivní verze

        // kopírovat odkaz
        this._fileMenu.addItem(this._createMenuItem(MenuItemFile.copyUrl)); // kopírovat odkaz na soubor

        // část podpisů
        const separator = new SeparatorMenuItem();
        this._fileMenu.addItem(separator);
        this._fileMenu.addItem(this._createMenuItem(MenuItemSigning.sign)); // Vytvořit podpis
        this._signMenuItemsSeparators.push(separator);
    }
}

// Menu pro režim empty - obsahuje pouze položky, které mají význam dokud nebyl nahrán soubor
export class FileMenuEmpty extends FileMenuBase {
    constructor() {
        super();
        this._createFileMenuItems();
        this.type = FileMenuType.empty;
    }

    private _createFileMenuItems(): void {
        this._registrMenuItem();

        this._fileMenu = new DropDownMenu();
        this._fileMenu.horizontalOpenPosition = DropDownMenuHorizontalOpen.right;
        this._fileMenu.addItem(this._createMenuItem(MenuItemFile.upload)); //Nahrát
        this._fileMenu.addItem(this._createMenuItem(MenuItemSigning.uploadAndSign)); // Nahrát a podepsat
    }
}

export enum FileEditStatus {
    empty,
    download,
    uploading,
    error,
    previewNotSupported,
    fileSizeExceed,
    unavailable,
}

export enum FileEditProgressStatus {
    progressValue,
    fail,
    complete,
    cancel,
}

export enum FileMenuType {
    edit,
    nonEdit,
    empty,
}
